import { useTranslation } from '@multaqa/i18n'
import { Badge } from '@multaqa/ui/components/ui/badge'

/**
 * The one status vocabulary for violations. Both the register
 * (`violations-columns.tsx`) and the detail screen (`violation-detail.tsx`)
 * render the pill from here, so the two screens cannot show the same row in
 * two different tones.
 */
export const VIOLATION_STATUSES = [
  'open',
  'acknowledged',
  'resolved',
  'proposed',
  'dismissed',
] as const

export type KnownViolationStatus = (typeof VIOLATION_STATUSES)[number]

const STATUS_TONES: Record<KnownViolationStatus, 'neutral' | 'warning' | 'success'> = {
  open: 'warning',
  acknowledged: 'neutral',
  resolved: 'success',
  proposed: 'warning',
  dismissed: 'neutral',
}

/**
 * `status` is an open string on the wire, and can be missing entirely on an
 * older row. Either case falls back to `open`, so a newer server never renders
 * a raw key or crashes on an undefined tone.
 */
export function asKnownViolationStatus(value: string | undefined): KnownViolationStatus {
  return value && (VIOLATION_STATUSES as readonly string[]).includes(value)
    ? (value as KnownViolationStatus)
    : 'open'
}

/**
 * The status pill, followed by an "Imported" mark for a violation the go-live
 * import loaded (f417 D13), so the register, the detail and the employee's
 * history mark it the same way.
 */
export function ViolationStatusBadge({
  status,
  origin,
}: {
  status: string | undefined
  origin?: string
}) {
  const { t } = useTranslation('hr-conduct')
  const known = asKnownViolationStatus(status)
  const pill = <Badge tone={STATUS_TONES[known]}>{t(`status.${known}`)}</Badge>

  if (origin !== 'imported') return pill

  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {pill}
      <Badge tone="neutral">{t('origin.imported')}</Badge>
    </span>
  )
}
